import * as Phaser from 'phaser';
import { trace, type BeamReflection, type MirrorState } from '../beam/tracer';
import type { LevelSpec } from '../level/schema';
import { LightSource } from '../entities/LightSource';
import { Mirror, ROTATION_STEP_DEG } from '../entities/Mirror';
import { SwitchEntity } from '../entities/Switch';
import { Exit } from '../entities/Exit';
import { HelperOverlay } from '../entities/HelperOverlay';
import { attachSlotInput } from './input';
import { getSDK } from '../sdk-bridge';
import { hasSeenHowToPlayColor, loadSettings, markComplete, type GameSettings } from '../progress';
import { Prism } from '../entities/Prism';
import { PrismHelperOverlay } from '../entities/PrismHelperOverlay';
import { beamColor, FONT, PALETTE, TYPE } from '../render/palette';
import { addAtmosphere } from '../render/atmosphere';
import { drawBeamSegment, drawShimmerLayer, type BeamRenderSegment } from '../render/beam';
import { drawDashedRoundedRect } from '../render/dashes';
import { PiecePicker } from './PiecePicker';
import type { PrismState } from '../beam/tracer';
import { LanguageDropdown } from './LanguageDropdown';
import { onLangChange } from '../i18n/helpers';
import { t, isRTL } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { ensureTouchHitArea } from './responsive-font';

const CELL = 64;
const WHEEL_STEP_DEG = 1;
const DRAG_SNAP_DEG = 1;
const DRAG_THRESHOLD_PX = 5;
const GHOST_ALPHA = 0.3;
const HINT_RIGHT_TOLERANCE_DEG = 0.5;

interface DragState {
  slotIndex: number;
  startX: number;
  startY: number;
  isDragging: boolean;
  wasJustPlaced: boolean;
}

export class PuzzleScene extends Phaser.Scene {
  private level!: LevelSpec;
  private mirrors: Map<number, Mirror> = new Map();
  private helpers: Map<number, HelperOverlay> = new Map();
  private ghosts: Map<number, Mirror> = new Map();
  private switches: Map<string, SwitchEntity> = new Map();
  private prisms: Map<number, Prism> = new Map();
  private prismHelpers: Map<number, PrismHelperOverlay> = new Map();
  private exit!: Exit;
  private beamGraphics!: Phaser.GameObjects.Graphics;
  private shimmerGraphics!: Phaser.GameObjects.Graphics;
  private shimmerSegments: BeamRenderSegment[] = [];
  private hintGraphics!: Phaser.GameObjects.Graphics;
  private hintLabels: Phaser.GameObjects.Text[] = [];
  private hudCounter!: Phaser.GameObjects.Text;
  // Lazily created in drawSlotRecesses(); reset to null on scene shutdown so
  // a level restart doesn't reference a destroyed Graphics from the prior run.
  private slotRecessGfx: Phaser.GameObjects.Graphics | null = null;
  private activePicker: PiecePicker | null = null;
  private startedAt = 0;
  private dragState: DragState | null = null;
  private settings: GameSettings = { hintsEnabled: true };
  // Touch-only rotation handle — null when not displayed.
  private rotationHandleGfx: Phaser.GameObjects.Graphics | null = null;
  private rotationHandleDot: Phaser.GameObjects.Arc | null = null;
  private rotationHandleSlot: number = -1;

  constructor() {
    super('puzzle');
  }

  init(data: { level: LevelSpec }): void {
    this.level = data.level;

    // First-time slice-2 onboarding: if this level uses any color mechanic
    // (a prism slot or a switch with required colors) and the player hasn't
    // seen the color how-to page yet, route to that page first. The how-to
    // scene then comes back here with the level data preserved.
    const isE2E =
      typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('e2e');
    const hasColorMechanic =
      this.level.prisms.available > 0 || this.level.switches.some((s) => s.requires.length > 0);
    if (!isE2E && hasColorMechanic && !hasSeenHowToPlayColor()) {
      this.scene.start('how-to-play-color', { nextLevel: this.level });
      return;
    }

    this.startedAt = Date.now();
    this.mirrors = new Map();
    this.helpers = new Map();
    this.ghosts = new Map();
    this.switches = new Map();
    this.prisms = new Map();
    this.prismHelpers = new Map();
    this.dragState = null;
    this.hintLabels = [];
    this.settings = loadSettings();
    // Scene-restart cleanup: GameObject references from the previous level
    // point at destroyed instances, so null them out and let create() rebuild.
    this.slotRecessGfx = null;
    this.shimmerSegments = [];
    this.activePicker = null;
    this.rotationHandleGfx = null;
    this.rotationHandleDot = null;
    this.rotationHandleSlot = -1;
  }

  create(): void {
    const { width, height } = this.level.grid;

    // Atmospheric background — sits behind every gameplay element.
    addAtmosphere(this, 0.7);

    // Grid background
    const g = this.add.graphics();
    g.fillStyle(PALETTE.GRID_BG).fillRect(0, 0, width * CELL, height * CELL);
    g.lineStyle(1, PALETTE.GRID_LINE, 0.6);
    for (let x = 0; x <= width; x++) g.lineBetween(x * CELL, 0, x * CELL, height * CELL);
    for (let y = 0; y <= height; y++) g.lineBetween(0, y * CELL, width * CELL, y * CELL);
    // Subtle inner glow on the play area edges so it feels "held by light."
    g.lineStyle(2, PALETTE.HUD_TITLE, 0.08);
    g.strokeRect(0, 0, width * CELL, height * CELL);

    // Walls — rounded body with a hairline top highlight.
    for (const w of this.level.walls ?? []) {
      const cx = (w.x + 0.5) * CELL;
      const cy = (w.y + 0.5) * CELL;
      const wallGfx = this.add.graphics();
      wallGfx.fillStyle(PALETTE.WALL, 1);
      wallGfx.fillRoundedRect(cx - CELL / 2, cy - CELL / 2, CELL, CELL, 3);
      wallGfx.lineStyle(1, PALETTE.WALL_SHADOW, 1);
      wallGfx.strokeRoundedRect(cx - CELL / 2, cy - CELL / 2, CELL, CELL, 3);
      wallGfx.fillStyle(PALETTE.WALL_HIGHLIGHT, 0.5);
      wallGfx.fillRect(cx - CELL / 2 + 2, cy - CELL / 2 + 2, CELL - 4, 1);
    }

    // Mirror + prism slot recesses (drawn before pieces so they sit below).
    this.drawSlotRecesses();

    // Light sources
    for (const src of this.level.lightSources) {
      new LightSource(this, CELL, src.pos.x, src.pos.y, src.direction);
    }

    // Switches
    for (const s of this.level.switches) {
      this.switches.set(s.id, new SwitchEntity(this, CELL, s.pos.x, s.pos.y, s.id, s.requires));
    }

    // Exit — clicking the exit cell completes the level when it's unlocked.
    this.exit = new Exit(this, CELL, this.level.exit.pos.x, this.level.exit.pos.y);
    this.exit.on('pointerdown', () => {
      if (this.exit.unlocked) void this.completeLevel();
    });

    // Invisible hit targets for every slot. Slots are generic — the picker
    // decides what device gets placed.
    const hotspots: Phaser.GameObjects.Rectangle[] = this.level.slots.map((slot) => {
      return this.add
        .rectangle((slot.x + 0.5) * CELL, (slot.y + 0.5) * CELL, 52, 52, 0xffffff, 0)
        .setOrigin(0.5);
    });

    attachSlotInput(this, hotspots, {
      onPointerOver: (i) => this.handleSlotHover(i, true),
      onPointerOut: (i) => this.handleSlotHover(i, false),
      onPointerDown: (i, p) => this.handleSlotDown(i, p),
      onWheel: (i, deltaY) => this.handleSlotWheel(i, deltaY),
    });
    // Expand slot hotspot hit areas to ≥44×44 on touch devices. The hotspots
    // are already 52×52 so this is defensive; the helper short-circuits when
    // bounds already satisfy the minimum.
    for (const hotspot of hotspots) {
      ensureTouchHitArea(hotspot, this, Phaser.Geom.Rectangle);
    }

    const onPointerMove = (p: Phaser.Input.Pointer): void => {
      if (this.dragState) this.handleDragMove(p);
    };
    const onPointerUp = (): void => {
      if (this.dragState) this.handleDragEnd();
    };
    this.input.on('pointermove', onPointerMove);
    this.input.on('pointerup', onPointerUp);
    // Phaser's InputPlugin is reset on scene shutdown so this is belt-and-
    // braces, but matching the explicit-cleanup pattern used elsewhere in the
    // codebase keeps the convention consistent — and would catch a future
    // accidental cross-scene listener registration.
    this.events.once('shutdown', () => {
      this.input.off('pointermove', onPointerMove);
      this.input.off('pointerup', onPointerUp);
      this.hideRotationHandle();
    });

    // Beam layers — static halo+bloom+core, plus an animated shimmer overlay.
    this.beamGraphics = this.add.graphics();
    this.beamGraphics.setBlendMode(Phaser.BlendModes.ADD);
    this.shimmerGraphics = this.add.graphics();
    this.shimmerGraphics.setBlendMode(Phaser.BlendModes.ADD);
    // Hint layer (angle arcs, drawn above the beam)
    this.hintGraphics = this.add.graphics();

    // HUD — level title (top-left in LTR, top-right in RTL).
    const titleKey = `level.${this.level.id}.title` as TranslationKey;
    const titleText = this.add.text(14, 8, t(titleKey).toUpperCase(), {
      color: FONT.TITLE,
      fontFamily: TYPE.SANS,
      fontSize: '14px',
      fontStyle: '500',
    });
    titleText.setLetterSpacing(4);
    this.hudCounter = this.add
      .text(width * CELL - 14, 8, '', {
        color: FONT.TEXT,
        fontFamily: TYPE.MONO,
        fontSize: '14px',
      })
      .setOrigin(1, 0);

    const applyTitleAndCounter = (): void => {
      titleText.setText(t(titleKey).toUpperCase());
      this.updateHudCounter();
      if (isRTL()) {
        titleText.setOrigin(1, 0);
        titleText.setPosition(width * CELL - 14, 8);
        this.hudCounter.setOrigin(0, 0);
        this.hudCounter.setPosition(14, 8);
      } else {
        titleText.setOrigin(0, 0);
        titleText.setPosition(14, 8);
        this.hudCounter.setOrigin(1, 0);
        this.hudCounter.setPosition(width * CELL - 14, 8);
      }
    };
    applyTitleAndCounter();
    onLangChange(this, applyTitleAndCounter);

    // Back-to-menu button — triangle in the bottom corner of the play area.
    const backToMenu = this.add
      .triangle(20, height * CELL - 20, 0, 8, 14, 0, 14, 16, PALETTE.HUD_DIM)
      .setInteractive({ useHandCursor: true });
    // Expand the hit area to ≥44×44 on touch; the rendered triangle is ~14×16
    // which is far too small for reliable finger taps.
    ensureTouchHitArea(backToMenu, this, Phaser.Geom.Rectangle);
    backToMenu.on('pointerover', () => backToMenu.setFillStyle(PALETTE.HUD_TITLE));
    backToMenu.on('pointerout', () => backToMenu.setFillStyle(PALETTE.HUD_DIM));
    backToMenu.on('pointerdown', () => {
      this.scene.start('menu');
    });

    const placeBack = (): void => {
      if (isRTL()) {
        backToMenu.x = width * CELL - 20;
        backToMenu.setRotation(Math.PI);
      } else {
        backToMenu.x = 20;
        backToMenu.setRotation(0);
      }
    };
    placeBack();
    onLangChange(this, placeBack);

    this.recompute();

    new LanguageDropdown(this);
  }

  /**
   * Render every empty slot as a dashed cyan rounded rect. Occupied slots
   * receive no recess so the piece reads cleanly against the grid.
   *
   * Called once at scene create and again whenever a piece is placed or
   * removed so the recess tracks occupancy.
   */
  private drawSlotRecesses(): void {
    if (!this.slotRecessGfx) {
      this.slotRecessGfx = this.add.graphics();
    }
    const g = this.slotRecessGfx;
    g.clear();

    // Recesses stay visible regardless of occupancy so the player can always
    // see where the level's slots are; pieces render on top.
    for (let i = 0; i < this.level.slots.length; i++) {
      const slot = this.level.slots[i]!;
      const occupied = this.mirrors.has(i) || this.prisms.has(i);
      this.paintSlotRecess(g, (slot.x + 0.5) * CELL, (slot.y + 0.5) * CELL, 52, occupied);
    }
  }

  private paintSlotRecess(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    size: number,
    occupied: boolean,
  ): void {
    const half = size / 2;
    // Dim translucent fill — occupied slots use a fainter wash so the device
    // reads as the primary subject inside the slot.
    g.fillStyle(PALETTE.SLOT_FILL, occupied ? 0.3 : 0.55);
    g.fillRoundedRect(cx - half, cy - half, size, size, 6);
    // Dashed cyan border — slightly dimmer when occupied.
    g.lineStyle(1, PALETTE.HUD_TITLE, occupied ? 0.18 : 0.35);
    drawDashedRoundedRect(g, cx - half, cy - half, size, size, 6, 4, 4);
  }

  override update(time: number): void {
    if (this.shimmerGraphics) {
      drawShimmerLayer(this.shimmerGraphics, this.shimmerSegments, time);
    }
  }

  private updateHudCounter(): void {
    if (!this.hudCounter) return;
    const totalItems = this.placedCount() + this.placedPrismCount();
    this.hudCounter.setText(
      t('puzzle.itemCounter', { used: totalItems, optimal: this.level.optimalItems }),
    );
  }

  // --- Slot interactions ---------------------------------------------------

  private handleSlotHover(slotIndex: number, entering: boolean): void {
    // Suppress hover effects mid-drag so a drag that crosses other slots
    // doesn't create flickering ghosts.
    if (this.dragState) return;
    if (this.mirrors.has(slotIndex) || this.prisms.has(slotIndex)) return;

    // Ghost-preview only when the slot would auto-place a mirror (the only
    // device available). When prisms are also available, hovering is ambiguous
    // so we don't preview anything — the picker reveals the choices on click.
    const showMirrorGhost = this.mirrorsRemaining() > 0 && this.prismsRemaining() === 0;
    if (!showMirrorGhost) {
      this.disposeGhost(slotIndex);
      return;
    }

    if (entering) {
      if (this.ghosts.has(slotIndex)) return;
      const slot = this.level.slots[slotIndex]!;
      const ghost = new Mirror({ scene: this, cellSize: CELL, gridX: slot.x, gridY: slot.y });
      ghost.setAlpha(GHOST_ALPHA);
      this.ghosts.set(slotIndex, ghost);
    } else {
      this.disposeGhost(slotIndex);
    }
  }

  private handleSlotDown(slotIndex: number, pointer: Phaser.Input.Pointer): void {
    // Mirror already placed → rotate / remove.
    if (this.mirrors.has(slotIndex)) {
      if (pointer.rightButtonDown()) {
        this.hideRotationHandle();
        this.removeMirror(slotIndex);
        return;
      }

      // Touch devices use the rotation handle ring instead of drag-rotate.
      if (this.sys.game.device.input.touch) {
        if (this.rotationHandleSlot === slotIndex) {
          // Second tap on the same mirror → dismiss the handle.
          this.hideRotationHandle();
        } else {
          this.showRotationHandle(slotIndex);
        }
        return;
      }

      // Desktop: existing drag-to-rotate path, unchanged.
      this.dragState = {
        slotIndex,
        startX: pointer.x,
        startY: pointer.y,
        isDragging: false,
        wasJustPlaced: false,
      };
      return;
    }
    // Prism already placed → cycle / remove.
    if (this.prisms.has(slotIndex)) {
      if (pointer.rightButtonDown()) {
        this.removePrism(slotIndex);
        return;
      }
      const prism = this.prisms.get(slotIndex)!;
      prism.rotateNext();
      const helper = this.prismHelpers.get(slotIndex);
      helper?.setInputDir(prism.inputDir, CELL);
      this.recompute();
      return;
    }

    // Empty slot — dismiss any open rotation handle, then figure out what to place.
    this.hideRotationHandle();
    if (this.activePicker) return;
    const mirrorsLeft = this.mirrorsRemaining();
    const prismsLeft = this.prismsRemaining();
    if (mirrorsLeft <= 0 && prismsLeft <= 0) return;

    const slot = this.level.slots[slotIndex]!;
    if (mirrorsLeft > 0 && prismsLeft <= 0) {
      this.disposeGhost(slotIndex);
      this.placeMirrorAt(slotIndex, slot.x, slot.y);
      return;
    }
    if (prismsLeft > 0 && mirrorsLeft <= 0) {
      this.placePrismAt(slotIndex, slot.x, slot.y);
      return;
    }
    // Both available — show the picker.
    this.disposeGhost(slotIndex);
    this.openPicker(slotIndex, slot.x, slot.y);
  }

  private mirrorsRemaining(): number {
    return this.level.mirrors.available - this.mirrors.size;
  }

  private prismsRemaining(): number {
    return this.level.prisms.available - this.prisms.size;
  }

  private placeMirrorAt(slotIndex: number, slotGridX: number, slotGridY: number): void {
    const mirror = new Mirror({ scene: this, cellSize: CELL, gridX: slotGridX, gridY: slotGridY });
    mirror.slotIndex = slotIndex;
    this.mirrors.set(slotIndex, mirror);
    if (this.level.showAngleHelper) {
      const overlay = new HelperOverlay(this, CELL);
      overlay.setPosition(mirror.x, mirror.y);
      overlay.setMirrorAngleDeg(mirror.angleDeg);
      this.helpers.set(slotIndex, overlay);
    }
    this.recompute();
  }

  private openPicker(slotIndex: number, gridX: number, gridY: number): void {
    const anchorX = (gridX + 0.5) * CELL;
    const anchorY = (gridY + 0.5) * CELL;
    this.activePicker = new PiecePicker(this, {
      anchorX,
      anchorY,
      mirrorEnabled: true,
      prismEnabled: true,
      onPick: (kind) => {
        this.dismissPicker();
        if (kind === 'mirror') this.placeMirrorAt(slotIndex, gridX, gridY);
        else this.placePrismAt(slotIndex, gridX, gridY);
      },
      onDismiss: () => this.dismissPicker(),
    });
  }

  private dismissPicker(): void {
    if (!this.activePicker) return;
    this.activePicker.destroy();
    this.activePicker = null;
  }

  private handleDragMove(pointer: Phaser.Input.Pointer): void {
    const ds = this.dragState!;
    const mirror = this.mirrors.get(ds.slotIndex);
    if (!mirror) {
      this.dragState = null;
      return;
    }
    const dx = pointer.x - ds.startX;
    const dy = pointer.y - ds.startY;
    if (!ds.isDragging && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
      ds.isDragging = true;
    }
    if (ds.isDragging) {
      const angDeg = (Math.atan2(pointer.y - mirror.y, pointer.x - mirror.x) * 180) / Math.PI;
      this.updateMirrorAngle(ds.slotIndex, angDeg);
    }
  }

  private handleDragEnd(): void {
    const ds = this.dragState!;
    this.dragState = null;
    const mirror = this.mirrors.get(ds.slotIndex);
    if (!mirror) return;

    if (ds.isDragging) {
      // Snap-to on release for predictable level design.
      const snapped = Math.round(mirror.angleDeg / DRAG_SNAP_DEG) * DRAG_SNAP_DEG;
      this.updateMirrorAngle(ds.slotIndex, snapped);
    } else if (!ds.wasJustPlaced) {
      // Tap on an existing mirror — keep the legacy 15° step as the
      // touch-friendly fallback for users without wheel/drag.
      this.rotateMirror(ds.slotIndex, ROTATION_STEP_DEG);
    }
    // wasJustPlaced && !isDragging: leave at default 45° (no-op).
  }

  private handleSlotWheel(slotIndex: number, deltaY: number): void {
    if (!this.mirrors.has(slotIndex)) return;
    const direction = deltaY > 0 ? 1 : -1;
    this.rotateMirror(slotIndex, direction * WHEEL_STEP_DEG);
  }

  // --- Mirror state mutators ----------------------------------------------

  private updateMirrorAngle(slotIndex: number, angleDeg: number): void {
    const mirror = this.mirrors.get(slotIndex);
    if (!mirror) return;
    mirror.setAngleDeg(angleDeg);
    const helper = this.helpers.get(slotIndex);
    if (helper) helper.setMirrorAngleDeg(mirror.angleDeg);
    this.recompute();
  }

  private rotateMirror(slotIndex: number, deltaDeg: number): void {
    const mirror = this.mirrors.get(slotIndex);
    if (!mirror) return;
    mirror.rotateBy(deltaDeg);
    const helper = this.helpers.get(slotIndex);
    if (helper) helper.setMirrorAngleDeg(mirror.angleDeg);
    this.recompute();
  }

  // --- Touch rotation handle ------------------------------------------------

  /**
   * Show a circular ring + draggable dot around the mirror at `slotIndex`.
   * Touch-only: exits immediately on non-touch devices so desktop behaviour
   * is completely unchanged.
   */
  private showRotationHandle(slotIndex: number): void {
    if (!this.sys.game.device.input.touch) return;

    this.hideRotationHandle();

    const mirror = this.mirrors.get(slotIndex);
    if (!mirror) return;

    this.rotationHandleSlot = slotIndex;

    const radius = CELL / 2 + 24; // 32 + 24 = 56 px from mirror centre

    // Ring graphic.
    const ring = this.add.graphics();
    ring.lineStyle(2, 0xffffff, 0.85);
    ring.strokeCircle(mirror.x, mirror.y, radius);
    this.rotationHandleGfx = ring;

    // Draggable dot positioned at current mirror angle.
    const angleRad = (mirror.angleDeg * Math.PI) / 180;
    const dot = this.add.circle(
      mirror.x + Math.cos(angleRad) * radius,
      mirror.y + Math.sin(angleRad) * radius,
      14,
      0xffffff,
      0.95,
    );
    dot.setInteractive({ useHandCursor: true, draggable: true });
    this.input.setDraggable(dot);
    this.rotationHandleDot = dot;

    const onDrag = (
      _p: unknown,
      obj: Phaser.GameObjects.GameObject,
      dragX: number,
      dragY: number,
    ): void => {
      if (obj !== dot) return;
      const current = this.mirrors.get(slotIndex);
      if (!current) return;
      const angleRadNow = Phaser.Math.Angle.Between(current.x, current.y, dragX, dragY);
      const angleDegNow = (angleRadNow * 180) / Math.PI;
      this.updateMirrorAngle(slotIndex, angleDegNow);
      // Reposition the dot to stay on the ring.
      dot.setPosition(
        current.x + Math.cos(angleRadNow) * radius,
        current.y + Math.sin(angleRadNow) * radius,
      );
    };
    this.input.on('drag', onDrag);

    // Store the listener so we can remove it precisely when the handle is hidden.
    (dot as Phaser.GameObjects.Arc & { _rotDragListener: typeof onDrag })._rotDragListener = onDrag;
  }

  /**
   * Destroy the rotation handle ring + dot and remove the drag listener.
   * Safe to call when no handle is shown.
   */
  private hideRotationHandle(): void {
    if (this.rotationHandleDot) {
      const dot = this.rotationHandleDot as Phaser.GameObjects.Arc & {
        _rotDragListener?: (
          _p: unknown,
          obj: Phaser.GameObjects.GameObject,
          dragX: number,
          dragY: number,
        ) => void;
      };
      if (dot._rotDragListener) {
        this.input.off('drag', dot._rotDragListener);
      }
      this.rotationHandleDot.destroy();
      this.rotationHandleDot = null;
    }
    this.rotationHandleGfx?.destroy();
    this.rotationHandleGfx = null;
    this.rotationHandleSlot = -1;
  }

  private removeMirror(slotIndex: number): void {
    const m = this.mirrors.get(slotIndex);
    if (!m) return;
    m.destroy();
    this.mirrors.delete(slotIndex);
    const helper = this.helpers.get(slotIndex);
    if (helper) {
      helper.destroy();
      this.helpers.delete(slotIndex);
    }
    this.recompute();
  }

  private disposeGhost(slotIndex: number): void {
    const ghost = this.ghosts.get(slotIndex);
    if (!ghost) return;
    ghost.destroy();
    this.ghosts.delete(slotIndex);
  }

  private placedCount(): number {
    return this.mirrors.size;
  }

  private placePrismAt(slotIndex: number, slotGridX: number, slotGridY: number): void {
    const prism = new Prism({ scene: this, cellSize: CELL, gridX: slotGridX, gridY: slotGridY });
    prism.slotIndex = slotIndex;
    this.prisms.set(slotIndex, prism);
    if (this.level.showPrismHelper) {
      const overlay = new PrismHelperOverlay(this, CELL);
      overlay.setPosition(prism.x, prism.y);
      overlay.setInputDir(prism.inputDir, CELL);
      this.prismHelpers.set(slotIndex, overlay);
      this.time.delayedCall(2500, () => overlay.fadeOut());
    }
    this.recompute();
  }

  private removePrism(slotIndex: number): void {
    const p = this.prisms.get(slotIndex);
    if (!p) return;
    p.destroy();
    this.prisms.delete(slotIndex);
    const helper = this.prismHelpers.get(slotIndex);
    if (helper) {
      helper.destroy();
      this.prismHelpers.delete(slotIndex);
    }
    this.recompute();
  }

  private placedPrismCount(): number {
    return this.prisms.size;
  }

  // --- Beam + HUD ----------------------------------------------------------

  private recompute(): void {
    const states: MirrorState[] = Array.from(this.mirrors.entries()).map(([slotIndex, m]) => ({
      slotIndex,
      angleDeg: m.angleDeg,
    }));
    const prismStates: PrismState[] = Array.from(this.prisms.entries()).map(([slotIndex, p]) => ({
      slotIndex,
      inputDir: p.inputDir,
    }));
    const result = trace(this.level, states, prismStates);

    this.beamGraphics.clear();
    const shimmer: BeamRenderSegment[] = [];
    for (const seg of result.segments) {
      const color = beamColor(seg.colors);
      const ax = seg.a.x * CELL;
      const ay = seg.a.y * CELL;
      const bx = seg.b.x * CELL;
      const by = seg.b.y * CELL;
      drawBeamSegment(this.beamGraphics, ax, ay, bx, by, color);
      shimmer.push({ a: { x: ax, y: ay }, b: { x: bx, y: by }, color });
    }
    this.shimmerSegments = shimmer;

    this.renderHints(result.reflections);

    for (const [id, sw] of this.switches) {
      sw.setLit(result.litSwitchIds.has(id));
    }
    const allLit = this.level.exit.requires.every((id) => result.litSwitchIds.has(id));
    this.exit.setUnlocked(allLit);

    this.updateHudCounter();
    this.drawSlotRecesses();
  }

  // --- Hint rendering ------------------------------------------------------

  private renderHints(reflections: BeamReflection[]): void {
    this.hintGraphics.clear();
    for (const label of this.hintLabels) label.destroy();
    this.hintLabels = [];

    if (!this.settings.hintsEnabled) return;

    this.hintGraphics.lineStyle(1, PALETTE.HINT, 0.8);
    for (const r of reflections) {
      this.drawReflectionHint(r);
    }
  }

  private drawReflectionHint(r: BeamReflection): void {
    const hx = r.point.x * CELL;
    const hy = r.point.y * CELL;

    // The V interior — the angle between the source-side ray (back along the incoming
    // beam) and the outgoing reflected ray. This is the "angle between the two beams"
    // that the player can actually see at the bounce.
    const sourceAngle = Math.atan2(-r.beamDir.y, -r.beamDir.x);
    const outgoingAngle = Math.atan2(r.reflectedDir.y, r.reflectedDir.x);
    const dot = Math.max(
      -1,
      Math.min(1, -r.beamDir.x * r.reflectedDir.x + -r.beamDir.y * r.reflectedDir.y),
    );
    const interiorRad = Math.acos(dot); // [0, π]
    const interiorDeg = Math.round((interiorRad * 180) / Math.PI);

    let typeKey: TranslationKey;
    if (Math.abs(interiorDeg - 90) <= HINT_RIGHT_TOLERANCE_DEG)
      typeKey = 'puzzle.hint.angleType.right';
    else if (interiorDeg < 90) typeKey = 'puzzle.hint.angleType.acute';
    else typeKey = 'puzzle.hint.angleType.obtuse';

    const arcRadius = 22;
    this.drawShortArc(hx, hy, arcRadius, sourceAngle, outgoingAngle);
    const labelText = t('puzzle.hint.label', { angle: interiorDeg, type: t(typeKey) });
    this.placeHintLabel(hx, hy, sourceAngle, outgoingAngle, arcRadius + 14, labelText);
  }

  private drawShortArc(cx: number, cy: number, r: number, startA: number, endA: number): void {
    const TWO_PI = Math.PI * 2;
    let diff = (((endA - startA) % TWO_PI) + TWO_PI) % TWO_PI;
    let anticlockwise = false;
    if (diff > Math.PI) {
      diff = TWO_PI - diff;
      anticlockwise = true;
    }
    const finalEnd = anticlockwise ? startA - diff : startA + diff;
    this.hintGraphics.beginPath();
    this.hintGraphics.arc(cx, cy, r, startA, finalEnd, anticlockwise);
    this.hintGraphics.strokePath();
  }

  private placeHintLabel(
    cx: number,
    cy: number,
    fromA: number,
    toA: number,
    distance: number,
    text: string,
  ): void {
    // Bisector via vector average — robust to angle wraparound.
    const fx = Math.cos(fromA),
      fy = Math.sin(fromA);
    const tx = Math.cos(toA),
      ty = Math.sin(toA);
    let bx = fx + tx,
      by = fy + ty;
    const blen = Math.hypot(bx, by);
    if (blen < 1e-6) {
      // 180° apart — fall back to a perpendicular direction.
      bx = -fy;
      by = fx;
    } else {
      bx /= blen;
      by /= blen;
    }
    const label = this.add
      .text(cx + bx * distance, cy + by * distance, text, {
        color: FONT.HINT,
        fontFamily: TYPE.MONO,
        fontSize: '11px',
        align: 'center',
      })
      .setOrigin(0.5);
    this.hintLabels.push(label);
  }

  public forceWinForTest(): void {
    for (const id of this.level.exit.requires) {
      this.switches.get(id)?.setLit(true);
    }
    this.exit.setUnlocked(true);
    void this.completeLevel();
  }

  private async completeLevel(): Promise<void> {
    const itemsUsed = this.placedCount() + this.placedPrismCount();
    const timeMs = Date.now() - this.startedAt;
    // Persist locally first so the menu's ✓ reflects this run even if the
    // platform reportScore call fails or hangs.
    markComplete(this.level.id, itemsUsed, timeMs);
    await getSDK().reportScore('luminary', {
      score: itemsUsed,
      metadata: {
        levelId: this.level.id,
        timeMs,
        // Wire field name kept as `optimalMirrors` for backward compat with the
        // slice-1 platform contract; semantically this is the "par count of
        // total items used (mirrors + prisms)" for slice 2.
        optimalMirrors: this.level.optimalItems,
        direction: 'lower',
      },
    });
    this.scene.start('menu');
  }
}
